<template>
  <div
    ref="containerRef"
    class="main-panel-container"
    :class="containerClasses"
    :style="containerStyles"
  >
    <div
      v-if="isCollapsed"
      class="main-panel-container__caption"
      @click="resetWidth"
    >
      <span>{{ caption }}</span>
    </div>
    <slot v-else />
    <div
      ref="resizerRef"
      class="main-panel-container__resizer"
      :class="resizerClasses"
    />
    <div class="main-panel-container__toggler">
      <UIButtonIcon
        v-tooltip.top="buttonTooltip"
        :trailing="buttonIcon"
        variant="gray"
        size="xxs"
        fill="light"
        @click="handleToggle"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, useTemplateRef, watch } from 'vue'
import {
  useDebounceFn,
  useDraggable,
  useElementBounding,
  useLocalStorage,
  useResizeObserver,
} from '@vueuse/core'

import { useHotKeys } from '@/plugins/hotKeys'

import { useUserSettingsStore } from '@/store/user/settings'

import { UIButtonIcon } from '@ui/buttons'
import PanelCloseIcon from '@/assets/images/icons/panel-close.svg'
import TreeIcon from '@/assets/images/icons/tree.svg'

const START_WIDTH = 350
const MIN_WIDTH = 240
const MIN_CONTENT_WIDTH = 370

type Props = {
  caption: string
}
defineProps<Props>()

const userSettingsStore = useUserSettingsStore()

const containerRef = useTemplateRef<HTMLDivElement>('containerRef')
const resizerRef = useTemplateRef('resizerRef')

const containerWidth = ref(MIN_WIDTH)

const isInited = ref(false)

const isTreeOpened = useLocalStorage<boolean>('TREE_OPENED', true)

const { left, width } = useElementBounding(containerRef)
const { isDragging, x } = useDraggable(resizerRef, {
  axis: 'x',
})

const storageWidth = computed({
  get() {
    return userSettingsStore.getAssetTreeWidth || START_WIDTH
  },
  set(value) {
    userSettingsStore.setAssetTreeWidth(value)
  },
})

const isCollapsed = computed(() => containerWidth.value < MIN_WIDTH)
const maxWidth = computed(() => document.body.clientWidth - MIN_CONTENT_WIDTH)

const containerClasses = computed(() => {
  return {
    'main-panel-container--collapsed': isCollapsed.value,
  }
})

const containerStyles = computed(() => {
  return isCollapsed.value
    ? undefined
    : {
      width: `${containerWidth.value}px`,
    }
})

const resizerClasses = computed(() => {
  return {
    'main-panel-container__resizer--dragging': isDragging.value,
    'main-panel-container__resizer--disabled': isCollapsed.value,
  }
})

const buttonTooltip = computed(() => {
  return isCollapsed.value ? 'Open Clients Tree' : 'Close Clients Tree'
})

const buttonIcon = computed(() => {
  return isCollapsed.value ? TreeIcon : PanelCloseIcon
})

const handleToggle = () => {
  isCollapsed.value ? resetWidth() : (containerWidth.value = 0)
}

const initWidth = () => {
  containerWidth.value = isTreeOpened.value ? storageWidth.value : 0
}

const resetWidth = () => {
  containerWidth.value = storageWidth.value
}

const updateStorageWidth = useDebounceFn((value: number) => {
  storageWidth.value = value < MIN_WIDTH ? MIN_WIDTH + 10 : value
}, 500)

const updateWidth = () => {
  let newWidth
  const offset = x.value > 0 ? x.value : left.value + width.value
  if (offset > maxWidth.value) {
    newWidth = maxWidth.value - left.value
  } else {
    newWidth = offset - left.value
  }
  containerWidth.value = newWidth
  updateStorageWidth(newWidth)
}

const updateWidthDebounced = useDebounceFn(updateWidth, 300)

useResizeObserver(document.body, () => {
  if (isCollapsed.value) return
  if (!isInited.value) {
    isInited.value = true
    return
  }
  updateWidthDebounced()
})

watch(isDragging, value => {
  if (value) {
    document.body.classList.add('select-none', 'cursor-col-resize')
  } else {
    document.body.classList.remove('select-none', 'cursor-col-resize')
  }
})

watch(x, () => {
  if (!isDragging.value) return
  updateWidth()
})

watch(isCollapsed, value => {
  isTreeOpened.value = !value
})

onBeforeMount(initWidth)

useHotKeys('tree', handleToggle)
</script>

<style>
.main-panel-container {
  @apply w-88;
  @apply relative;
  @apply flex flex-col shrink-0;
  @apply bg-card;
  @apply border-r border-border;

  .ui-button__icon {
    @apply size-4;
  }
  
  &__caption {
    @apply absolute inset-0;
    @apply text-xs font-medium;
    @apply tracking-wide leading-none;
    @apply whitespace-nowrap;

    span {
      @apply absolute top-1/2 left-1/2;
      @apply -translate-x-1/2 -translate-y-1/2;
      @apply origin-center;
      @apply rotate-90;
    }
  }
  
  &--collapsed {
    @apply w-7.25;
    @apply text-gray-400 dark:text-gray-400;
    @apply hover:text-gray-500 dark:hover:text-gray-200;
    @apply cursor-pointer;
  }


  &__resizer {
    @apply w-1.25;
    @apply absolute top-13 bottom-1 -right-0.75;
    @apply bg-primary-selected;
    @apply cursor-col-resize;
    @apply opacity-0;
    @apply z-10;

    &--dragging,
    &:hover {
      @apply opacity-40;
    }

    &--disabled {
      @apply opacity-0;
      @apply pointer-events-none;
    }
  }

  &__toggler {
    @apply absolute top-[.7rem] left-full;
    @apply bg-card;
    @apply border border-l-0 border-border;
    @apply rounded-r;
    @apply z-20;

    .main-panel-container--collapsed & {
      @apply left-1/2 -translate-x-1/2;
      @apply mt-px;
      @apply border-none;
      @apply hover:z-41;
    }

    .ui-button {
      @apply -ml-1;
      @apply hover:bg-transparent;
      @apply z-11;

      .main-panel-container--collapsed & {
        @apply ml-0;
      }
    }
  }
}
</style>
